import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import heroBg from '../assets/hero-bg.svg';
import { API_BASE, getImageUrl } from '../config';
import { ArrowRight, Scissors, Sparkles } from 'lucide-react';

export default function Hero({ t }) {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/settings`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(() => setSettings({ hero_image: '', about_image: '' }));
  }, []);

  const bgImage = useMemo(() => {
    if (!settings) return heroBg;
    return settings.hero_image ? getImageUrl(settings.hero_image) : heroBg;
  }, [settings]);

  const aboutImage = useMemo(() => {
    if (!settings) return null;
    return settings.about_image
      ? getImageUrl(settings.about_image)
      : 'https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=1000&q=80';
  }, [settings]);

  const excerpt = useMemo(() => {
    const text = t.about.text || '';
    if (text.length <= 250) return text;

    const trimmed = text.substring(0, 250);
    return trimmed.substring(0, trimmed.lastIndexOf(' ')) + '...';
  }, [t.about.text]);

  return (
    <div className="flex flex-col bg-white">
      <HeroBanner t={t} bgImage={bgImage} />
      <AboutPreview t={t} excerpt={excerpt} aboutImage={aboutImage} />
      <ServicesMarquee />
    </div>
  );
}

/* ---------------------------------------------------
   HERO BANNER
--------------------------------------------------- */
function HeroBanner({ t, bgImage }) {
  return (
    <section
      className="relative text-white py-16 md:py-20 px-4 min-h-[50vh] md:min-h-[60vh] flex items-center justify-center bg-cover bg-center bg-no-repeat transition-all duration-700"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="absolute inset-0 bg-black/40 bg-gradient-to-b from-black/60 via-transparent to-brand-900/50"></div>

      <div className="relative max-w-7xl mx-auto text-center z-10 px-4 pb-8">
        <div className="inline-flex items-center justify-center p-1.5 mb-6 bg-white/10 backdrop-blur-xl rounded-full border border-white/30 px-6 animate-fade-in-up">
          <Sparkles className="h-4 w-4 mr-2 text-brand-300" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-50">
            {t.hero.badge}
          </span>
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 tracking-tighter drop-shadow-2xl leading-[1.1] font-serif italic">
          {t.hero.title}
        </h1>

        <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto font-medium drop-shadow-lg text-gray-100 opacity-90 leading-relaxed">
          {t.hero.subtitle}
        </p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------
   ABOUT PREVIEW
--------------------------------------------------- */
function AboutPreview({ t, excerpt, aboutImage }) {
  return (
    <section className="relative z-20 -mt-32 px-4 pb-16">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden border border-gray-100 flex flex-col lg:flex-row">

          <div className="lg:w-2/5 relative h-48 lg:h-auto overflow-hidden">
            <img
              src={aboutImage}
              alt={t.about.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-1000 hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-900/30 to-transparent"></div>
          </div>

          <div className="lg:w-3/5 p-6 md:p-8 space-y-4 flex flex-col justify-center bg-white relative">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-brand-600 mb-1">
                <div className="w-8 h-0.5 bg-brand-600"></div>
                <span className="text-[10px] font-black uppercase tracking-[0.3em]">
                  {t.about.badge}
                </span>
              </div>

              <h2 className="text-2xl md:text-3xl font-black text-slate-900 font-serif italic">
                {t.about.title}
              </h2>
            </div>

            <p className="text-base text-slate-600 leading-relaxed whitespace-pre-line font-medium italic opacity-80">
              "{excerpt}"
            </p>

            <div className="pt-1">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-50 text-brand-700 rounded-xl font-black uppercase tracking-widest text-[10px] group hover:bg-brand-600 hover:text-white transition-all shadow-sm active:scale-95"
              >
                {t.about.ctaAbout || 'En savoir plus'}
                <ArrowRight className="h-3.5 w-3.5 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="absolute top-6 right-6 opacity-5">
              <Scissors className="h-20 w-20 text-slate-900" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------
   SERVICES MARQUEE
--------------------------------------------------- */
function ServicesMarquee() {
  return (
    <section className="bg-slate-50 py-12 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <div className="flex items-center justify-center gap-8 opacity-40 overflow-hidden whitespace-nowrap">
          {Array.from({ length: 10 }).map((_, i) => (
            <span
              key={i}
              className="text-slate-400 font-black uppercase tracking-[0.4em] text-[10px]"
            >
              Coiffure de Prestige • Salon à Domicile • Excellence
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
